import './MessagePreviewModule.scss';

import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { FC, useMemo } from 'react';

import { MessagePreviewExtra } from '../../analyzeMessageContent';
import { MicrolinkPreview, AttachmentsPreview, ChecklistPreview, DetailsPreview, EmailPreview } from './subcomponents';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface MessagePreviewModuleProps {
  extras: MessagePreviewExtra[];
}

type MessagePreviewType = MessagePreviewExtra['type'];

const detectionLabels: Record<MessagePreviewType, string> = {
  email: 'Email thread detected',
  arcade: 'Arcade walkthrough',
  link: 'Link preview',
  attachments: 'Attachments',
  checklist: 'Checklist',
  details: 'Details',
};

const MessagePreviewModule: FC<MessagePreviewModuleProps> = ({ extras }) => {
  const prefix = prefixClass('q2-message-preview');
  const { enableImagePreviewQueue } = useFlags();
  const types = useMemo(() => Array.from(new Set(extras.map(extra => extra.type))) as MessagePreviewType[], [extras]);

  const classes = types.reduce(
    (acc, type) => ({
      ...acc,
      [`is-${type}`]: true,
    }),
    {} as Record<string, boolean>
  );

  return (
    <div className={classNames(prefix())}>
      <div className={classNames(prefix('module'), classes)}>
        {extras.map((extra, index) => {
          switch (extra.type) {
            case 'details':
              return <DetailsPreview key={`details-preview-${index}`} content={extra.data} request={extra.request} />;
            case 'email': {
              return <EmailPreview key={`${extra.type}-preview-${index}`} extra={extra} />;
            }
            case 'checklist':
              return <ChecklistPreview key={`checklist-preview-${index}`} checklist={extra.checklist} />;
            case 'arcade':
            case 'link':
              return <MicrolinkPreview key={`${extra.type}-preview-${index}`} extra={extra} />;
            case 'attachments':
              return enableImagePreviewQueue ? (
                <AttachmentsPreview key={`attachments-preview-${index}`} attachments={extra.attachments} />
              ) : null;
            default:
              return null;
          }
        })}
      </div>
    </div>
  );
};

export default MessagePreviewModule;
